import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  // Clock3,
  // Database,
  // LockKeyhole,
  Loader2,
  Mail,
  Send,
  // ShieldCheck,
  // VolumeX,
} from "lucide-react";
import { toast } from "sonner";

import type {
  BillingInterval,
  EnterpriseCompanySize,
  EnterpriseExpectedVolume,
  QuoteRequest,
} from "@/lib/http/billing";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
// import {
//   Card,
//   CardContent,
//   CardDescription,
//   CardHeader,
//   CardTitle,
// } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { isLoggedIn } from "@/lib/http";
import { userInfoQueryOptions } from "@/lib/queries/auth";
import {
  plansQueryOptions,
  quoteQueryOptions,
  subscriptionMeQueryOptions,
  useCheckoutMutation,
  useEnterpriseInquiryMutation,
} from "@/lib/queries/billing";
import {
  BILLING_CHECKOUT_EXPECTATION_KEY,
  MAX_BUSINESS_MINUTES,
  MINUTE_INCREMENT,
  MIN_BUSINESS_MINUTES,
  asBillingError,
  isCurrentSubscriptionPlan,
  readApiErrorMessage,
} from "@/lib/http/billing";

export const Route = createFileRoute("/_app/pricing/")({
  component: PricingPage,
});

const freeFeatures = [
  "3 team members",
  "60 minutes of meeting analysis per month",
  "1 active policy",
  "Every detection — AI speech, deepfake voice, sensitive data",
  "Google Meet, Teams and Zoom",
  "Calendar auto-join",
];

const businessFeatures = [
  "Unlimited policies, incidents and reports",
  "Unlimited seats within your plan, at no extra cost",
  "Encrypted at rest and in transit; SSO available",
];

const enterpriseFeatures = [
  "Unlimited seats",
  "Committed-volume minute pricing",
  "SSO, SCIM and audit export",
  "Region pinning, custom or zero retention, and BYO encryption keys",
  "SIEM streaming, API access and full audit trail",
  "SOC 2 Type II and GDPR; DPA, security review and pen-test reports",
  "Named contact and onboarding support",
];

// const securityFeatures = [
//   {
//     icon: LockKeyhole,
//     title: "Encrypted end to end",
//     description:
//       "Recordings, transcripts and analysis are encrypted in transit and at rest. Only your workspace can decrypt them.",
//   },
//   {
//     icon: Database,
//     title: "Your data stays yours",
//     description:
//       "Meeting content is never used to train models, and is never shared with other customers.",
//   },
//   {
//     icon: Clock3,
//     title: "Retention you control",
//     description:
//       "Recordings are deleted automatically at the end of your retention window — 30 days on Free, configurable on paid plans.",
//   },
//   {
//     icon: VolumeX,
//     title: "Redaction built in",
//     description:
//       "Sensitive spans are beeped out of stored audio, so a shared recording never carries the raw disclosure.",
//   },
//   {
//     icon: ShieldCheck,
//     title: "Least-privilege access",
//     description:
//       "Role-based access, per-meeting sharing scopes, and every action written to an immutable audit log.",
//   },
//   {
//     icon: Building2,
//     title: "Compliance ready",
//     description:
//       "SOC 2 Type II and GDPR aligned, with regional hosting and a DPA available.",
//   },
// ];

const faqItems = [
  {
    question: "What counts as a minute?",
    answer:
      "Wall-clock minutes the bot spends in a meeting, rounded to the nearest minute per call. If the bot is removed and re-sent, only time actually in the call is counted.",
  },
  {
    question: "What happens when I run out of minutes?",
    answer:
      "Any call already in progress finishes normally — it is analysed to the end and you keep the full transcript, findings and report for it. The next meeting is locked until you top up or your cycle resets. Nothing already captured is ever deleted, and on Business you can raise the cap at any time.",
  },
  {
    question: "Are features limited on the Free plan?",
    answer:
      "Free has every detection and the full reporting the paid plans have. The limits are three seats, sixty minutes a month, one active policy, and a fixed 30-day retention window.",
  },
  {
    question: "Do unused minutes roll over?",
    answer: "No. The allowance resets on the first of each month.",
  },
  {
    question: "How is a seat defined?",
    answer:
      "Anyone who signs in. Meeting participants are not seats — you only pay for people using the console.",
  },
  {
    question: "Where is meeting data stored?",
    answer:
      "In your chosen region, encrypted at rest. Recordings follow the retention window set in Settings; the default is 180 days.",
  },
];

function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  }).format(value);
}

function formatHours(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (!hours) return `${remainingMinutes}min`;
  return `${hours}h${remainingMinutes ? `, ${remainingMinutes}min` : ""}`;
}

type UsageSliderProps = {
  label: string;
  detail?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  formatValue: (value: number) => string;
};

function UsageSlider({
  label,
  detail,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
}: UsageSliderProps) {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold text-[#535862] dark:text-[#CECFD2]">
        {label}
        {detail && (
          <span className="font-normal text-muted-foreground"> {detail}</span>
        )}
      </div>
      <div className="mb-2 flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[22px] leading-none font-semibold tracking-[-0.04em] tabular-nums">
          {formatValue(value)}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          onClick={() => onChange(Math.max(min, value - step))}
          aria-label={`Decrease ${label.toLowerCase()}`}
        >
          <ChevronLeft className="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-7"
          onClick={() => onChange(Math.min(max, value + step))}
          aria-label={`Increase ${label.toLowerCase()}`}
        >
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
      <input
        className="h-[5px] w-full cursor-pointer appearance-none rounded-full outline-none focus-visible:ring-3 focus-visible:ring-[#DD2590]/20 [&::-moz-range-thumb]:size-[15px] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-[#DD2590] [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-sm [&::-webkit-slider-thumb]:size-[15px] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[#DD2590] [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        style={{
          background: `linear-gradient(to right, #DD2590 0%, #DD2590 ${percentage}%, var(--secondary) ${percentage}%, var(--secondary) 100%)`,
        }}
        aria-label={label}
      />
      <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
        <span>{formatValue(min)}</span>
        <span>{formatValue(max)}</span>
      </div>
    </div>
  );
}

function FeatureList({ features }: { features: Array<string> }) {
  return (
    <ul className="mt-4 flex flex-1 flex-col gap-2.5">
      {features.map((feature) => (
        <li
          key={feature}
          className="flex gap-2 text-[13px] leading-[1.45] text-[#535862] dark:text-[#CECFD2]"
        >
          <Check className="mt-0.5 size-3.5 shrink-0 stroke-[2.7] text-emerald-600 dark:text-emerald-400" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

function PlanCard({
  children,
  featured = false,
}: {
  children: React.ReactNode;
  featured?: boolean;
}) {
  return (
    <section
      className={cn(
        "relative flex min-w-0 flex-col rounded-2xl border bg-card p-5 transition duration-150 hover:-translate-y-0.5 hover:border-[#D5D7DA] dark:hover:border-[#535862]",
        featured &&
          "border-[#DD2590] shadow-[0_0_0_1px_#DD2590,0_20px_50px_-28px_rgba(221,37,144,0.7)] hover:border-[#DD2590]",
      )}
    >
      {featured && (
        <span className="absolute -top-2.5 left-5 rounded-md bg-[#DD2590] px-2 py-0.5 text-[10px] font-bold tracking-[0.05em] text-white">
          MOST POPULAR
        </span>
      )}
      {children}
    </section>
  );
}

type EnterpriseForm = {
  name: string;
  email: string;
  company: string;
  companySize: EnterpriseCompanySize;
  minutes: EnterpriseExpectedVolume;
  notes: string;
};

type EnterpriseFormErrors = Partial<
  Record<"name" | "email" | "company" | "notes", string>
>;

const enterpriseCompanySizes: ReadonlyArray<{
  value: EnterpriseCompanySize;
  label: string;
}> = [
  { value: "SIZE_1_50", label: "1–50" },
  { value: "SIZE_51_200", label: "51–200" },
  { value: "SIZE_201_1000", label: "201–1,000" },
  { value: "SIZE_1001_5000", label: "1,001–5,000" },
  { value: "SIZE_5000_PLUS", label: "5,000+" },
];

const enterpriseExpectedVolumes: ReadonlyArray<{
  value: EnterpriseExpectedVolume;
  label: string;
}> = [
  { value: "MINUTES_UNDER_25K", label: "Under 25,000" },
  { value: "MINUTES_25K_PLUS", label: "25,000+" },
  { value: "MINUTES_50K_PLUS", label: "50,000+" },
  { value: "MINUTES_100K_PLUS", label: "100,000+" },
  { value: "NOT_SURE", label: "Not sure yet" },
];

const initialEnterpriseForm: EnterpriseForm = {
  name: "",
  email: "",
  company: "",
  companySize: "SIZE_201_1000",
  minutes: "MINUTES_25K_PLUS",
  notes: "",
};

function validateEnterpriseForm(form: EnterpriseForm): EnterpriseFormErrors {
  const errors: EnterpriseFormErrors = {};
  const name = form.name.trim();
  const email = form.email.trim();
  const company = form.company.trim();

  if (!name) errors.name = "Enter your full name.";
  else if (name.length > 255) errors.name = "Use 255 characters or fewer.";

  if (!email) errors.email = "Enter your work email.";
  else if (email.length > 255 || !/^\S+@\S+\.\S+$/.test(email)) {
    errors.email = "Enter a valid work email.";
  }

  if (!company) errors.company = "Enter your company name.";
  else if (company.length > 255) {
    errors.company = "Use 255 characters or fewer.";
  }

  if (form.notes.length > 4000) {
    errors.notes = "Use 4,000 characters or fewer.";
  }

  return errors;
}

function EnterpriseDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState(initialEnterpriseForm);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<Set<keyof EnterpriseForm>>(new Set());
  const [submitError, setSubmitError] = useState<string | null>(null);
  const inquiry = useEnterpriseInquiryMutation();
  const errors = validateEnterpriseForm(form);
  const canSubmit = Object.keys(errors).length === 0;

  function updateForm<TField extends keyof EnterpriseForm>(
    field: TField,
    value: EnterpriseForm[TField],
  ) {
    setForm((current) => ({ ...current, [field]: value }));
    if (submitError) setSubmitError(null);
  }

  function touch(field: keyof EnterpriseForm) {
    setTouched((current) => new Set(current).add(field));
  }

  async function submitEnterpriseInquiry() {
    const validationErrors = validateEnterpriseForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setTouched(new Set(["name", "email", "company", "notes"]));
      return;
    }

    setSubmitError(null);
    try {
      const response = await inquiry.mutateAsync({
        full_name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        company: form.company.trim(),
        company_size: form.companySize,
        expected_volume: form.minutes,
        notes: form.notes.trim(),
      });
      setSubmitted(true);
      toast.success(response.message || "Enterprise request sent");
    } catch (error) {
      const message =
        (await readApiErrorMessage(error)) ??
        "We couldn't send your request. Please try again.";
      setSubmitError(message);
      toast.error(message);
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && inquiry.isPending) return;
    onOpenChange(nextOpen);
    if (!nextOpen) {
      window.setTimeout(() => {
        setForm(initialEnterpriseForm);
        setSubmitted(false);
        setTouched(new Set());
        setSubmitError(null);
        inquiry.reset();
      }, 200);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="mb-1 flex size-9 items-center justify-center rounded-lg bg-[#FCE7F6] text-[#C11574] dark:bg-[#DD2590]/15 dark:text-[#F670C7]">
            <Building2 className="size-4" />
          </div>
          <DialogTitle>Talk to us about Enterprise</DialogTitle>
          <DialogDescription>
            Tell us the shape of your deployment and we&apos;ll come back with a
            quote.
          </DialogDescription>
        </DialogHeader>

        {submitted ? (
          <div
            className="flex flex-col items-center px-4 py-8 text-center"
            role="status"
          >
            <div className="mb-4 flex size-11 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Check className="size-5 stroke-[2.5]" />
            </div>
            <h3 className="font-semibold">Request sent</h3>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              We&apos;ll email <strong>{form.email}</strong> within one business
              day with a quote for {form.company}.
            </p>
          </div>
        ) : (
          <form
            className="grid gap-4 sm:grid-cols-2"
            onSubmit={(event) => {
              event.preventDefault();
              void submitEnterpriseInquiry();
            }}
            noValidate
            aria-busy={inquiry.isPending}
          >
            <div className="space-y-1.5">
              <Label htmlFor="enterprise-name">Full name</Label>
              <Input
                id="enterprise-name"
                value={form.name}
                onChange={(event) => updateForm("name", event.target.value)}
                onBlur={() => touch("name")}
                placeholder="Jordan Alvarez"
                autoComplete="name"
                maxLength={255}
                aria-invalid={Boolean(touched.has("name") && errors.name)}
                aria-describedby={
                  touched.has("name") && errors.name
                    ? "enterprise-name-error"
                    : undefined
                }
                disabled={inquiry.isPending}
                required
              />
              {touched.has("name") && errors.name && (
                <p
                  id="enterprise-name-error"
                  className="text-xs text-destructive"
                >
                  {errors.name}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enterprise-email">Work email</Label>
              <Input
                id="enterprise-email"
                type="email"
                value={form.email}
                onChange={(event) => updateForm("email", event.target.value)}
                onBlur={() => touch("email")}
                placeholder="jordan@company.com"
                autoComplete="email"
                maxLength={255}
                aria-invalid={Boolean(touched.has("email") && errors.email)}
                aria-describedby={
                  touched.has("email") && errors.email
                    ? "enterprise-email-error"
                    : undefined
                }
                disabled={inquiry.isPending}
                required
              />
              {touched.has("email") && errors.email && (
                <p
                  id="enterprise-email-error"
                  className="text-xs text-destructive"
                >
                  {errors.email}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enterprise-company">Company</Label>
              <Input
                id="enterprise-company"
                value={form.company}
                onChange={(event) => updateForm("company", event.target.value)}
                onBlur={() => touch("company")}
                placeholder="Company name"
                autoComplete="organization"
                maxLength={255}
                aria-invalid={Boolean(touched.has("company") && errors.company)}
                aria-describedby={
                  touched.has("company") && errors.company
                    ? "enterprise-company-error"
                    : undefined
                }
                disabled={inquiry.isPending}
                required
              />
              {touched.has("company") && errors.company && (
                <p
                  id="enterprise-company-error"
                  className="text-xs text-destructive"
                >
                  {errors.company}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enterprise-size">Company size</Label>
              <select
                id="enterprise-size"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                value={form.companySize}
                disabled={inquiry.isPending}
                onChange={(event) =>
                  updateForm(
                    "companySize",
                    event.target.value as EnterpriseCompanySize,
                  )
                }
              >
                {enterpriseCompanySizes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enterprise-minutes">
                Expected minutes / month
              </Label>
              <select
                id="enterprise-minutes"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                value={form.minutes}
                disabled={inquiry.isPending}
                onChange={(event) =>
                  updateForm(
                    "minutes",
                    event.target.value as EnterpriseExpectedVolume,
                  )
                }
              >
                {enterpriseExpectedVolumes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="enterprise-notes">Anything we should know?</Label>
              <Textarea
                id="enterprise-notes"
                rows={3}
                value={form.notes}
                onChange={(event) => updateForm("notes", event.target.value)}
                onBlur={() => touch("notes")}
                placeholder="Deployment region, SSO provider, procurement timeline…"
                maxLength={4000}
                aria-invalid={Boolean(touched.has("notes") && errors.notes)}
                aria-describedby={
                  touched.has("notes") && errors.notes
                    ? "enterprise-notes-error"
                    : undefined
                }
                disabled={inquiry.isPending}
              />
              {touched.has("notes") && errors.notes && (
                <p
                  id="enterprise-notes-error"
                  className="text-xs text-destructive"
                >
                  {errors.notes}
                </p>
              )}
            </div>
            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive sm:col-span-2"
              >
                {submitError}
              </div>
            )}
            <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center">
              <p className="flex-1 text-xs text-muted-foreground">
                We reply within one business day. No call required to get a
                quote.
              </p>
              <Button type="submit" disabled={!canSubmit || inquiry.isPending}>
                {inquiry.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Send className="size-4" />
                )}
                {inquiry.isPending ? "Sending…" : "Send request"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PricingPage() {
  const navigate = useNavigate();
  const [minutes, setMinutes] = useState(6_040);
  const [seats] = useState(50);
  const [interval, setInterval] = useState<BillingInterval>("MONTHLY");
  const [debouncedRequest, setDebouncedRequest] = useState<QuoteRequest>({
    minutes,
    seats,
    interval: "MONTHLY",
  });
  const [enterpriseDialogOpen, setEnterpriseDialogOpen] = useState(false);
  const [billingConfigured, setBillingConfigured] = useState(true);
  const loggedIn = isLoggedIn() === true;
  const user = useQuery({ ...userInfoQueryOptions, enabled: loggedIn });
  const hasOrganization = Boolean(loggedIn && user.data?.organization_id);
  const subscription = useQuery({
    ...subscriptionMeQueryOptions,
    enabled: hasOrganization,
  });
  const plans = useQuery(plansQueryOptions);
  const quote = useQuery(quoteQueryOptions(debouncedRequest));
  const checkout = useCheckoutMutation();
  const freePlan = plans.data?.find((plan) => plan.code === "FREE");
  const businessPlan = plans.data?.find((plan) => plan.is_self_serve);
  const freeIsCurrent = isCurrentSubscriptionPlan(
    subscription.data,
    freePlan?.code,
  );
  const businessIsCurrent = isCurrentSubscriptionPlan(
    subscription.data,
    businessPlan?.code,
  );
  const isCheckingSubscription = hasOrganization && subscription.isPending;

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setDebouncedRequest({ minutes, seats, interval: "MONTHLY" }),
      300,
    );
    return () => window.clearTimeout(timeout);
  }, [minutes, seats]);

  useEffect(() => {
    if (!plans.error && !quote.error) return;
    let active = true;
    void asBillingError(plans.error ?? quote.error).then((error) => {
      if (active && error?.code === "BILLING_NOT_CONFIGURED") {
        setBillingConfigured(false);
      }
    });
    return () => {
      active = false;
    };
  }, [plans.error, quote.error]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "cancel") return;
    sessionStorage.removeItem(BILLING_CHECKOUT_EXPECTATION_KEY);
    window.history.replaceState(null, "", "/pricing");
  }, []);

  const selectedTotal =
    interval === "MONTHLY"
      ? quote.data?.monthly_total_cents
      : quote.data?.yearly_total_cents;
  const undiscountedYearlyTotal = quote.data
    ? quote.data.monthly_total_cents * 12
    : undefined;

  async function startCheckout() {
    if (!loggedIn) {
      await navigate({
        to: "/auth/login",
        search: { redirect: "/pricing", redirectSearch: {} },
      });
      return;
    }
    if (
      businessIsCurrent ||
      !user.data?.org_admin ||
      !user.data.organization_id ||
      !quote.data
    ) {
      return;
    }
    try {
      const origin = window.location.origin;
      const session = await checkout.mutateAsync({
        seats: quote.data.seats,
        minutes: quote.data.minutes,
        interval,
        success_url: `${origin}/settings/billing?checkout=success`,
        cancel_url: `${origin}/pricing?checkout=cancel`,
      });
      sessionStorage.setItem(
        BILLING_CHECKOUT_EXPECTATION_KEY,
        JSON.stringify({
          minutes: session.minutes,
          seats: session.seats,
          interval: session.interval,
        }),
      );
      window.location.href = session.url;
    } catch (error) {
      toast.error(
        (await readApiErrorMessage(error)) ??
          "Checkout is temporarily unavailable. Please try again.",
      );
    }
  }

  return (
    <main className="mx-auto w-full max-w-[1560px] px-4 py-6 md:px-6">
      <header className="mx-auto mt-1 mb-8 max-w-[620px] text-center">
        <h1 className="text-[27px] leading-tight font-semibold tracking-[-0.04em]">
          Pricing that follows your meeting volume
        </h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          Every plan includes every detection, and every plan runs in the same
          secure, encrypted environment. You pay only for the minutes analysed —
          seats are included.
        </p>
      </header>

      <div
        className="mx-auto mb-6 grid max-w-xs grid-cols-2 gap-2 rounded-lg bg-muted p-1"
        role="group"
        aria-label="Billing interval"
      >
        {(["MONTHLY", "YEARLY"] as const).map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={interval === value ? "default" : "ghost"}
            onClick={() => setInterval(value)}
          >
            {value === "MONTHLY" ? "Monthly" : "Yearly (20% OFF)"}
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,250px),1fr))] items-stretch gap-4">
        <PlanCard>
          <h2 className="text-[15px] font-semibold">
            {freePlan?.name ?? "Free"}
          </h2>
          <p className="mt-1 mb-4 text-xs text-muted-foreground">
            For trying it on real calls.
          </p>
          <div className="flex items-end gap-1.5">
            <span className="text-[34px] leading-none font-semibold tracking-[-0.05em] tabular-nums">
              $0
            </span>
            <span className="pb-0.5 text-[13px] text-muted-foreground">
              / month
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {freePlan
              ? `${freePlan.included_minutes?.toLocaleString() ?? "Unlimited"} minutes · ${freePlan.max_seats?.toLocaleString() ?? "Unlimited"} seats`
              : "Renews every month, forever."}
          </p>
          <FeatureList features={freeFeatures} />
          <Button className="mt-5 w-full" variant="outline" size="lg" disabled>
            {freeIsCurrent ? "Current plan" : "Free plan"}
          </Button>
        </PlanCard>

        <PlanCard featured>
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold">
              {businessPlan?.name ?? "Business"}
            </h2>
            <Badge
              variant="outline"
              className="border-[#DD2590]/25 bg-[#FCE7F6] text-[#C11574] dark:bg-[#DD2590]/15 dark:text-[#F670C7]"
            >
              Usage-based
            </Badge>
          </div>
          <p className="mt-1 mb-4 text-xs text-muted-foreground">
            You pay for minutes analysed. Seats are included.
          </p>

          <div className="flex flex-wrap items-end gap-1.5">
            <span className="text-[34px] leading-none font-semibold tracking-[-0.05em] tabular-nums">
              {selectedTotal === undefined
                ? "—"
                : formatCurrency(selectedTotal / 100)}
            </span>
            {interval === "YEARLY" && undiscountedYearlyTotal !== undefined ? (
              <span
                className="pb-0.5 text-base text-muted-foreground tabular-nums line-through"
                title="Price without the yearly discount"
              >
                {formatCurrency(undiscountedYearlyTotal / 100)}
              </span>
            ) : null}
            <span className="pb-0.5 text-[13px] text-muted-foreground">
              / {interval === "MONTHLY" ? "month" : "year"}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {(quote.data?.minutes ?? minutes).toLocaleString()} minutes/monthly
            ({formatHours(quote.data?.minutes ?? minutes)}) · Up to 50 seats
            included
          </p>

          <div className="mt-5 space-y-4">
            <UsageSlider
              label="Meeting minutes per month"
              min={MIN_BUSINESS_MINUTES}
              max={MAX_BUSINESS_MINUTES}
              step={MINUTE_INCREMENT}
              value={minutes}
              onChange={setMinutes}
              formatValue={(value) =>
                `${value.toLocaleString()} min (${formatHours(value)})`
              }
            />
          </div>

          <FeatureList features={businessFeatures} />
          <Button
            className="mt-5 w-full"
            size="lg"
            disabled={
              checkout.isPending ||
              quote.isPending ||
              businessIsCurrent ||
              isCheckingSubscription ||
              !billingConfigured ||
              (loggedIn &&
                (!user.data?.org_admin || !user.data.organization_id))
            }
            onClick={() => void startCheckout()}
          >
            {businessIsCurrent
              ? "Current plan"
              : isCheckingSubscription
                ? "Checking current plan…"
                : checkout.isPending
                  ? "Opening checkout…"
                  : !billingConfigured
                    ? "Purchases unavailable in this environment"
                    : !loggedIn
                      ? "Sign in to subscribe"
                      : user.data?.org_admin
                        ? "Subscribe to Business"
                        : "Ask an administrator to subscribe"}
          </Button>
        </PlanCard>

        <PlanCard>
          <h2 className="text-[15px] font-semibold">Enterprise</h2>
          <p className="mt-1 mb-4 text-xs text-muted-foreground">
            For regulated teams and large volume.
          </p>
          <div className="text-[27px] leading-none font-semibold tracking-[-0.04em]">
            Custom
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Volume rate below $0.05 / min.
          </p>
          <FeatureList features={enterpriseFeatures} />
          <Button
            className="mt-5 w-full"
            variant="outline"
            size="lg"
            onClick={() => setEnterpriseDialogOpen(true)}
          >
            <Mail className="size-4" />
            Contact us
          </Button>
        </PlanCard>
      </div>

      {/* <Card className="mt-5 gap-0 py-0">
        <CardHeader className="border-b py-4">
          <div className="flex items-start gap-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#FCE7F6] text-[#C11574] dark:bg-[#DD2590]/15 dark:text-[#F670C7]">
              <LockKeyhole className="size-4" />
            </div>
            <div>
              <CardTitle className="text-sm">
                Security and data handling on every plan
              </CardTitle>
              <CardDescription className="mt-1 text-xs">
                The same protections apply whether you are on Free, Business or
                Enterprise.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,230px),1fr))] gap-5 py-5">
          {securityFeatures.map((feature) => {
            const FeatureIcon = feature.icon;
            return (
              <div key={feature.title}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex size-6 items-center justify-center rounded-md bg-[#FCE7F6] text-[#C11574] dark:bg-[#DD2590]/15 dark:text-[#F670C7]">
                    <FeatureIcon className="size-3.5" />
                  </span>
                  <h3 className="text-[13px] font-semibold">{feature.title}</h3>
                </div>
                <p className="text-xs leading-[1.55] text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </CardContent>
      </Card> */}

      <section className="mx-auto mt-7 max-w-[760px]">
        <div className="mb-4 text-center">
          <h2 className="text-xl font-semibold tracking-[-0.03em]">
            Frequently asked
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            The things teams ask before they switch a plan on.
          </p>
        </div>
        <Accordion
          type="single"
          collapsible
          defaultValue="faq-0"
          className="space-y-2"
        >
          {faqItems.map((item, index) => (
            <AccordionItem
              key={item.question}
              value={`faq-${index}`}
              className="rounded-xl border bg-card px-4 last:border-b"
            >
              <AccordionTrigger className="py-3.5 text-[13px] font-semibold hover:no-underline">
                {item.question}
              </AccordionTrigger>
              <AccordionContent className="pb-3.5 text-[13px] leading-5 text-muted-foreground">
                {item.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <EnterpriseDialog
        open={enterpriseDialogOpen}
        onOpenChange={setEnterpriseDialogOpen}
      />
    </main>
  );
}
